import { useState, useRef } from 'react'
import { Upload, Loader2, Wrench } from 'lucide-react'
import type { Tool } from '../../types'
import ToolLibraryEditor from '../mission-control/ToolLibraryEditor'
import { importToolsFromFile } from '../../services/api'
import EmptyState from '../ui/EmptyState'

interface ToolLibrariesPanelProps {
  tools: Tool[]
  onChangeTools: (tools: Tool[]) => void
}

export default function ToolLibrariesPanel({ tools, onChangeTools }: ToolLibrariesPanelProps) {
  const [operationType, setOperationType] = useState<'milling' | 'turning'>('milling')
  const [importing, setImporting] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const TURNING_TYPES = new Set(['OD Insert', 'ID Boring Bar', 'Parting', 'Threading', 'Face Insert'])

  // Infer operation type from existing tools on first render
  useState(() => {
    if (tools.some(t => TURNING_TYPES.has(t.type))) {
      setOperationType('turning')
    }
  })

  const handleImportClick = () => {
    setImportError(null)
    fileInputRef.current?.click()
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    e.target.value = ''
    setImporting(true)
    setImportError(null)

    try {
      const result = await importToolsFromFile(file)

      if (result.tools.length === 0) {
        setImportError('No tools found in the file. Make sure it contains a tool inventory.')
        setImporting(false)
        return
      }

      // Merge imported tools into existing inventory with unique numbers
      const usedNumbers = new Set(tools.map(t => t.number))
      const newTools = result.tools.map(t => {
        let num = t.number ?? 1
        while (usedNumbers.has(num)) num++
        usedNumbers.add(num)
        return { ...t, id: crypto.randomUUID(), number: num }
      })

      const hasTurning = newTools.some(t => TURNING_TYPES.has(t.type))
      if (hasTurning) setOperationType('turning')

      onChangeTools([...tools, ...newTools])
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Import failed'
      setImportError(message)
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Operation type toggle + Import */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-slate-500">Tool types for</span>
          {(['milling', 'turning'] as const).map(type => (
            <button
              key={type}
              type="button"
              onClick={() => setOperationType(type)}
              className={`
                px-3 py-1.5 rounded-full font-mono text-xs capitalize transition-all duration-150
                ${operationType === type
                  ? 'bg-cyan-500/15 border border-cyan-500/40 text-cyan-400'
                  : 'border border-cyan-500/15 text-slate-500 hover:text-slate-300'
                }
              `}
            >
              {type}
            </button>
          ))}
        </div>

        <button
          onClick={handleImportClick}
          disabled={importing}
          className="
            flex items-center gap-2 px-3 py-1.5 rounded-lg
            font-mono text-xs text-amber-400 border border-amber-500/30
            hover:border-amber-400 hover:bg-amber-500/5
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-all duration-150
          "
        >
          {importing ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Parsing tools...
            </>
          ) : (
            <>
              <Upload className="w-3.5 h-3.5" />
              Import from File
            </>
          )}
        </button>
      </div>

      {/* Import error */}
      {importError && (
        <div className="px-3 py-2 rounded-lg border border-red-500/30 bg-red-500/5">
          <p className="font-mono text-xs text-red-400">{importError}</p>
        </div>
      )}

      {/* Tool editor */}
      {tools.length === 0 && !importing ? (
        <EmptyState
          icon={Wrench}
          title="No tools in your inventory"
          description="Add tools manually or import from a file."
          action={{ label: 'Import from File', onClick: handleImportClick }}
        />
      ) : (
        <ToolLibraryEditor
          tools={tools}
          operationType={operationType}
          onChange={onChangeTools}
        />
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv,.doc,.docx,.txt,.tsv"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  )
}
